// frontend/src/hooks/bookingManagement/useBookingSubmission.js

import { useCallback } from 'react';
import axios from '../../axiosConfig';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { formatDateISO, parseLocalDate } from '../../utils/dateTimeFormatter';
import { normalizeBookingTime, shortenAddress } from '../../utils/bookingTimeUtils';
import { findApplicableSchedule } from '../../utils/bookingScheduleUtils';

/**
 * Capture the exact occurrence identity rendered to the rider. The backend
 * re-resolves this map under locks; these values are an optimistic-concurrency
 * token, not client authority to select a trip or schedule winner.
 */
export const buildExpectedOccurrenceState = ({
  dates,
  normalizedTime,
  scheduleId,
  occurrencePreview = {}
}) => {
  const requestedOccurrenceDates = [...new Set(dates)].sort();
  const occurrenceTime = String(normalizedTime || '').slice(0, 5);
  const occurrenceAt = (date) => {
    const entries = occurrencePreview?.[date] || [];
    const fixed = entries.find((entry) => (
      entry.type === 'fixed'
      && String(entry.time || '').slice(0, 5) === occurrenceTime
      && Number(entry.scheduleId) === Number(scheduleId)
    ));
    if (fixed) return fixed;

    const window = entries.find((entry) => (
      entry.type === 'on_call'
      && entry.startTime <= occurrenceTime
      && occurrenceTime <= entry.endTime
      && Number(entry.scheduleId) === Number(scheduleId)
    ));
    if (!window) return null;
    const existing = (window.existingTrips || []).find(
      (entry) => String(entry.time).slice(0, 5) === occurrenceTime
    );
    return existing || { ...window, time: occurrenceTime, tripId: null };
  };
  const occurrenceEntries = requestedOccurrenceDates
    .map((date) => [date, occurrenceAt(date)])
    .filter(([, entry]) => entry);

  return {
    requestedOccurrenceDates,
    occurrenceEntries,
    initialOccurrenceDates: occurrenceEntries.map(([date]) => date),
    expectedTripIdsByDate: Object.fromEntries(
      occurrenceEntries.map(([date, entry]) => [
        date,
        Number.isInteger(Number(entry.tripId)) && Number(entry.tripId) > 0
          ? Number(entry.tripId)
          : null
      ])
    )
  };
};

/**
 * Custom hook for handling booking submission logic
 *
 * This hook encapsulates all the complex logic for validating, preparing, and submitting bookings,
 * including handling different booking modes (single, recurring, all available dates), time validation,
 * advance booking restrictions, and admin bypass functionality.
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.booking - Current booking form state
 * @param {Function} params.validateBooking - Validation function for booking data
 * @param {boolean} params.canBookRecurring - Whether recurring bookings are allowed
 * @param {boolean} params.bookAllAvailableDates - Whether booking all available dates
 * @param {Array<string>} params.selectedDates - Array of selected dates (YYYY-MM-DD format)
 * @param {Array<string>} params.availableDates - Array of available dates (YYYY-MM-DD format)
 * @param {number} params.minimumAdvanceHours - Minimum hours required before departure
 * @param {string} params.minimumAdvanceBookingScope - Scope for minimum advance hours: 'all_stops' or 'start_only'
 * @param {boolean} params.isAdminBooking - Whether this is an admin creating a booking for a user
 * @param {boolean} params.bypassAdvanceHours - Whether admin is bypassing advance hour restrictions
 * @param {Function} params.getCurrentRoute - Function to get the current route object
 * @param {number} params.maximumLuggagePerRider - Maximum luggage pieces per rider (0 = hide field)
 * @param {boolean} params.bookingRequiredConfirmation - Whether bookings require admin confirmation
 * @param {string} params.translatedVerificationPrompt - Translated verification prompt text
 * @param {Array<Object>} params.appliedSchedules - Array of applicable schedules for the route
 * @param {Object} params.t - i18n translation function
 * @param {number} params.entityId - Current entity ID
 * @param {number} params.currentUserId - Current logged-in user ID
 * @param {number|null} params.selectedUserId - Selected user ID (for admin bookings)
 * @param {string} params.entityTimezone - IANA timezone for the entity
 * @param {Function} params.setNormalizedBookingTime - State setter for normalized booking time
 * @param {Function} params.setShowPastDepartureDialog - State setter for past departure dialog
 * @param {Function} params.setPastDepartureMessage - State setter for past departure message
 * @param {Function} params.setIsConfirmModalOpen - State setter for confirmation modal
 * @param {Function} params.setCurrentBookingDetails - State setter for current booking details
 * @param {Function} params.setIsProcessing - State setter for processing state
 * @param {Function} params.setStatus - State setter for booking status
 * @param {Function} params.setIsWaitlisted - State setter for waitlist status
 * @param {Function} params.setBooking - State setter for booking form data
 * @param {Function} params.setVerificationError - State setter for verification error
 * @param {Function} params.setContactPrefilled - State setter for contact prefilled flag
 * @param {Function} params.setSelectedDates - State setter for selected dates
 * @param {Function} params.setAvailableTimeSlots - State setter for available time slots
 * @param {Function} params.setBookAllAvailableDates - State setter for book all available dates flag
 * @param {Function} params.fetchUserBookings - Function to refresh user bookings list
 * @param {Function} params.onBookingCreated - Optional callback after successful booking creation
 * @param {boolean} params.additionalVerificationEnabled - Whether additional verification is enabled
 *
 * @returns {Object} Object containing handleSubmit and submitBooking functions
 */
const useBookingSubmission = ({
  booking,
  validateBooking,
  canBookRecurring,
  bookAllAvailableDates,
  selectedDates,
  availableDates,
  minimumAdvanceHours,
  minimumAdvanceBookingScope,
  isAdminBooking,
  bypassAdvanceHours,
  getCurrentRoute,
  maximumLuggagePerRider,
  bookingRequiredConfirmation,
  translatedVerificationPrompt,
  appliedSchedules,
  t,
  entityId,
  currentUserId,
  selectedUserId,
  entityTimezone,
  setNormalizedBookingTime,
  setShowPastDepartureDialog,
  setPastDepartureMessage,
  setIsConfirmModalOpen,
  setCurrentBookingDetails,
  setIsProcessing,
  setStatus,
  setIsWaitlisted,
  setBooking,
  setVerificationError,
  setContactPrefilled,
  setSelectedDates,
  setAvailableTimeSlots,
  setBookAllAvailableDates,
  fetchUserBookings,
  onBookingCreated,
  additionalVerificationEnabled,
  occurrencePreview = {},
  refreshSlotAvailability
}) => {
  /**
   * Handles the initial form submission and prepares booking details for confirmation
   *
   * This function:
   * 1. Validates all booking fields
   * 2. Formats dates to YYYY-MM-DD
   * 3. Normalizes booking time to HH:mm format
   * 4. Checks if departure time has passed
   * 5. Validates minimum advance booking hours
   * 6. Prepares booking details for confirmation modal
   *
   * @param {Event} e - Form submit event
   */
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Step 1: Validate all booking fields
    if (!validateBooking()) return;

    // Step 2: Determine which dates to book based on booking mode
    const dates = bookAllAvailableDates ? availableDates :
                  canBookRecurring ? selectedDates :
                  [formatDateISO(booking.date)];

    // Step 3: Normalize time to HH:mm format
    const scheduleValue = typeof booking.schedule === 'string' ? booking.schedule : booking.schedule?.time;
    const normalizedTime = normalizeBookingTime(scheduleValue);

    if (!normalizedTime) {
      toast.error(t('invalidTimeSlot'));
      return;
    }

    // Step 4: Check departure time and minimum advance hours
    if (dates[0]) {
      // Fallback to browser timezone if entity timezone not set (more reliable than UTC)
      const tz = entityTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const dateValue = typeof dates[0] === 'string' ? dates[0].split('T')[0] : formatDateISO(dates[0]);

      const departureDateTime = moment.tz(
        `${dateValue} ${normalizedTime}`,
        'YYYY-MM-DD HH:mm:ss',
        tz
      );
      const now = moment.tz(tz);
      const hoursUntilDeparture = departureDateTime.isValid()
        ? departureDateTime.diff(now, 'hours', true)
        : NaN;

      // Note: We do NOT block submission when departure time has passed.
      // The backend handles this with the confirmPastDeparture flow and
      // pickupStopPassed validation. This allows "book after departure if
      // pickup not passed" to work correctly.

      // Check minimum advance hours if configured
      // Determine if restriction applies based on scope
      const route = getCurrentRoute();
      const pickupStopId = parseInt(booking.pickup);
      const firstStopId = route?.waypoints?.sort((a, b) => a.sequence_number - b.sequence_number)?.[0]?.id;
      const isStartLocation = firstStopId && pickupStopId === firstStopId;
      const shouldEnforceAdvanceBooking = minimumAdvanceHours > 0 && (
        minimumAdvanceBookingScope === 'all_stops' ||
        (minimumAdvanceBookingScope === 'start_only' && isStartLocation)
      );

      if (shouldEnforceAdvanceBooking && hoursUntilDeparture < minimumAdvanceHours) {
        if (isAdminBooking && bypassAdvanceHours) {
          console.info('Admin bypassing advance booking restriction', {
            scope: minimumAdvanceBookingScope,
            isStartLocation
          });
        } else {
          toast.error(t('bookingTooCloseToDepature', { hours: minimumAdvanceHours }));
          return;
        }
      }
    }

    // Store normalized time for use in submitBooking
    setNormalizedBookingTime(normalizedTime);

    // Step 5: Prepare booking details for confirmation modal
    const bookingDetails = {
      routeName: getCurrentRoute()?.name,
      bookingMode: bookAllAvailableDates ? 'all' : canBookRecurring ? 'multiple' : 'single',
      // Keep raw ISO dates to avoid locale-dependent parsing differences between environments
      dates: [...dates],
      time: booking.schedule,
      timeValue: typeof booking.schedule === 'string' ? booking.schedule : booking.schedule?.time,
      passengers: maximumLuggagePerRider > 0
        ? `${booking.riders} ${t('riders')}, ${booking.luggage} ${t('luggage')}`
        : `${booking.riders} ${t('riders')}`,
      pickup: (() => {
        const pickupStop = getCurrentRoute()?.waypoints?.find(s => s.id === parseInt(booking.pickup));
        return pickupStop ? `${pickupStop.name}: ${shortenAddress(pickupStop.address)}` : '';
      })(),
      dropoff: (() => {
        const dropoffStop = getCurrentRoute()?.waypoints?.find(s => s.id === parseInt(booking.dropoff));
        return dropoffStop ? `${dropoffStop.name}: ${shortenAddress(dropoffStop.address)}` : '';
      })(),
      contactNumber: booking.contactNumber,
      specialRequests: booking.specialRequests,
      entityVerificationValue: booking.entityVerificationValue?.trim() || '',
      verificationPrompt: translatedVerificationPrompt,
      startDate: new Date(availableDates[0] + 'T00:00:00'),
      endDate: appliedSchedules[0].end_date,
      bookingStatus: bookingRequiredConfirmation ? 'pending' : 'confirmed'
    };

    setIsConfirmModalOpen(true);
    setCurrentBookingDetails(bookingDetails);
  }, [
    validateBooking,
    bookAllAvailableDates,
    availableDates,
    canBookRecurring,
    selectedDates,
    booking,
    minimumAdvanceHours,
    minimumAdvanceBookingScope,
    isAdminBooking,
    bypassAdvanceHours,
    getCurrentRoute,
    maximumLuggagePerRider,
    bookingRequiredConfirmation,
    translatedVerificationPrompt,
    appliedSchedules,
    t,
    setNormalizedBookingTime,
    setIsConfirmModalOpen,
    setCurrentBookingDetails,
    entityTimezone
  ]);

  /**
   * Submits the booking to the API after user confirmation
   *
   * This function:
   * 1. Finds the applicable schedule for the selected dates/time
   * 2. Validates advance booking restrictions again (with admin bypass support)
   * 3. Prepares comprehensive booking data payload
   * 4. Sends POST request to create booking
   * 5. Handles success/error responses
   * 6. Resets form on success
   * 7. Refreshes booking list
   *
   * @param {Object|string|null} estimatedPickupTime - Estimated pickup time or confirmation object
   */
  const submitBooking = useCallback(async (estimatedPickupTime = null) => {
    setIsProcessing(true);
    const requestDates = bookAllAvailableDates ? availableDates
      : canBookRecurring ? selectedDates
      : [formatDateISO(booking.date)];
    try {
      // Step 1: Determine which dates to book
      const dates = requestDates;

      // Step 2: Find applicable schedule based on first date and selected time
      const firstDate = new Date(dates[0] + 'T00:00:00');
      const schedule = appliedSchedules.find(
        (candidate) => Number(candidate.id) === Number(booking.selectedScheduleId)
      ) || findApplicableSchedule(firstDate, booking.schedule, appliedSchedules);

      if (!schedule) {
        toast.error(t('noValidScheduleFoundForSelectedDatesAndTime'));
        return;
      }

      // Step 3: Validate and normalize booking time
      const normalizedTime = normalizeBookingTime(booking.schedule);
      if (!normalizedTime) {
        toast.error(t('invalidTimeSlot'));
        setIsProcessing(false);
        return;
      }

      // Step 4: Re-validate minimum advance booking hours
      if (dates[0]) {
        // Calculate departure time by combining first booking date with booking time
        const departureDateTime = new Date(`${dates[0]}T${normalizedTime}`);
        const now = new Date();
        const hoursUntilDeparture = (departureDateTime - now) / (1000 * 60 * 60);

        // Determine if restriction applies based on scope
        const route = getCurrentRoute();
        const pickupStopId = parseInt(booking.pickup);
        const firstStopId = route?.waypoints?.sort((a, b) => a.sequence_number - b.sequence_number)?.[0]?.id;
        const isStartLocation = firstStopId && pickupStopId === firstStopId;
        const shouldEnforceAdvanceBooking = minimumAdvanceHours > 0 && (
          minimumAdvanceBookingScope === 'all_stops' ||
          (minimumAdvanceBookingScope === 'start_only' && isStartLocation)
        );

        if (shouldEnforceAdvanceBooking && hoursUntilDeparture < minimumAdvanceHours) {
          if (isAdminBooking && bypassAdvanceHours) {
            console.info('Admin bypassed advance booking hours restriction', {
              adminId: currentUserId,
              selectedUserId,
              scope: minimumAdvanceBookingScope,
              isStartLocation
            });
          } else {
            toast.error(t('bookingTooCloseToDepature', { hours: minimumAdvanceHours }));
            setIsProcessing(false);
            return;
          }
        }
      }

      // Step 5: Calculate end date based on booking type
      const endDate = bookAllAvailableDates
        ? parseLocalDate(schedule.end_date) // Use schedule end date for all available days
        : new Date(dates[dates.length - 1] + 'T00:00:00'); // Use last selected date for multiple/single

      const confirmPastDeparture = Boolean(estimatedPickupTime?.confirmPastDeparture);
      const resolvedEstimatedPickupTime = confirmPastDeparture
        ? (estimatedPickupTime?.estimatedPickupTime ?? null)
        : estimatedPickupTime;

      const {
        requestedOccurrenceDates,
        occurrenceEntries,
        initialOccurrenceDates,
        expectedTripIdsByDate
      } = buildExpectedOccurrenceState({
        dates,
        normalizedTime,
        scheduleId: schedule.id,
        occurrencePreview
      });
      if (!bookAllAvailableDates && occurrenceEntries.length !== requestedOccurrenceDates.length) {
        toast.error(t('bookingStopEligibility.stale'));
        setIsProcessing(false);
        return;
      }
      if (bookAllAvailableDates && occurrenceEntries.length === 0) {
        toast.error(t('bookingStopEligibility.unresolved'));
        setIsProcessing(false);
        return;
      }
      const resolvedBookingType =
        (bookAllAvailableDates || dates.length > 1) ? 'recurring' : 'single';

      // Step 6: Prepare comprehensive booking data payload
      const bookingData = {
        userId: isAdminBooking ? parseInt(selectedUserId, 10) : currentUserId,
        scheduleId: schedule.id,
        RouteId: parseInt(booking.route),
        pickupStopId: parseInt(booking.pickup),
        dropoffStopId: parseInt(booking.dropoff),
        bookingType: resolvedBookingType,
        recurringType: bookAllAvailableDates ? "all_available_days" : dates.length > 1 ? "selected_days" : null, // all available days if all available dates, selected days if multiple dates selected, null if single date selected
        bookingDate: dates[0], // Send date as YYYY-MM-DD string
        endDate: formatDateISO(endDate), // Send as YYYY-MM-DD string
        selectedDates: dates.length > 1 ? dates : null,  // Array of YYYY-MM-DD strings, null if only one date selected
        bookingTime: normalizedTime,
        estimatedPickupTime: resolvedEstimatedPickupTime,
        riders: parseInt(booking.riders),
        luggages: maximumLuggagePerRider > 0 ? parseInt(booking.luggage) : 0,
        requestAccessibleVehicle: Boolean(booking.requestAccessibleVehicle),
        contactNumber: booking.contactNumber,
        specialRequests: booking.specialRequests || '',
        entityVerificationValue: booking.entityVerificationValue?.trim() || '',
        bookingStatus: bookingRequiredConfirmation ? 'pending' : 'confirmed',
        expectedTripId: resolvedBookingType === 'single'
          ? (
            Number.isInteger(Number(booking.selectedTripId))
              && Number(booking.selectedTripId) > 0
              ? Number(booking.selectedTripId)
              : (expectedTripIdsByDate[requestedOccurrenceDates[0]] ?? null)
          )
          : undefined,
        ...(resolvedBookingType === 'recurring' && {
          expectedTripIdsByDate,
          initialOccurrenceDates
        }),
        // Pass confirmation flag if user already confirmed past departure time
        ...(confirmPastDeparture && { confirmPastDeparture: true }),
        // Include admin-specific fields
        ...(isAdminBooking && {
          bookedByAdminId: currentUserId,
          bypassAdvanceBookingRestriction: bypassAdvanceHours
        })
      };

      // Step 7: Send POST request to create booking
      const response = await axios.post(
        `/api/bookings/${entityId}`,
        bookingData
      );

      // Step 8: Handle success response
      if (response.data.success) {
        toast.success(t('bookingSubmittedSuccessfully'));
        setStatus(bookingRequiredConfirmation ? t('pending') : t('confirmed'));
        setIsConfirmModalOpen(false);

        // Step 9: Reset all form state
        setSelectedDates([]);
        setAvailableTimeSlots([]);
        setBookAllAvailableDates(false);
        setStatus(null);
        setIsWaitlisted(false);

        // Reset booking form
        setBooking({
          route: '',
          pickup: '',
          dropoff: '',
          date: null,
          schedule: '',
          riders: '',
          luggage: '',
          requestAccessibleVehicle: false,
          contactNumber: '',
          specialRequests: '',
          entityVerificationValue: ''
        });

        setNormalizedBookingTime(null);
        setVerificationError('');
        setContactPrefilled(false);

        // Step 10: Refresh booking list for non-admin users
        if (!isAdminBooking) {
          await fetchUserBookings();
        }

        // Step 11: Call onBookingCreated callback if provided
        if (typeof onBookingCreated === 'function') {
          onBookingCreated();
        }
      }
    } catch (error) {
      console.error(t('failedToCreateBooking'), error);

      // Step 12: Handle error responses
      const responseData = error.response?.data;
      const errorMessage = responseData?.error || t('failedToCreateBooking');

      if (['BOOKING_TRIP_STOP_UNAVAILABLE', 'BOOKING_OCCURRENCE_STALE']
        .includes(responseData?.code)) {
        setBooking((previous) => ({
          ...previous,
          schedule: '',
          selectedTripId: null,
          selectedScheduleId: null
        }));
        if (typeof refreshSlotAvailability === 'function') {
          await refreshSlotAvailability(
            parseInt(booking.route, 10),
            requestDates[0],
            booking.pickup,
            booking.dropoff,
            []
          );
        }
        toast.error(
          responseData.code === 'BOOKING_TRIP_STOP_UNAVAILABLE'
            ? t('bookingStopEligibility.unavailable')
            : t('bookingStopEligibility.stale')
        );
        return;
      }

      // Check if backend requires confirmation (e.g., past departure time)
      if (responseData?.requiresConfirmation && responseData?.confirmationType === 'pastDeparture') {
        // Show the past departure confirmation dialog
        setPastDepartureMessage(t('departureTimeAlreadyPassed'));
        setShowPastDepartureDialog(true);
        setIsProcessing(false);
        return;
      }

      const requiredMessage = t('entityVerificationValueRequiredMessage');
      const tooLongMessage = t('entityVerificationValueTooLongMessage', { maxLength: 255 });

      // Set verification error if it's a verification-related error
      if (additionalVerificationEnabled &&
          (errorMessage === requiredMessage || errorMessage === tooLongMessage)) {
        setVerificationError(errorMessage);
      }

      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  }, [
    bookAllAvailableDates,
    availableDates,
    canBookRecurring,
    selectedDates,
    booking,
    appliedSchedules,
    minimumAdvanceHours,
    minimumAdvanceBookingScope,
    isAdminBooking,
    bypassAdvanceHours,
    currentUserId,
    selectedUserId,
    maximumLuggagePerRider,
    bookingRequiredConfirmation,
    entityId,
    additionalVerificationEnabled,
    t,
    setIsProcessing,
    setStatus,
    setIsConfirmModalOpen,
    setSelectedDates,
    setAvailableTimeSlots,
    setBookAllAvailableDates,
    setIsWaitlisted,
    setBooking,
    setNormalizedBookingTime,
    setVerificationError,
    setContactPrefilled,
    setPastDepartureMessage,
    setShowPastDepartureDialog,
    fetchUserBookings,
    onBookingCreated,
    getCurrentRoute
    ,occurrencePreview
    ,refreshSlotAvailability
  ]);

  return {
    handleSubmit,
    submitBooking
  };
};

export default useBookingSubmission;
